<script setup>
import {
  onMounted,
  reactive,
  ref,
  defineProps,
  defineEmits,
  computed,
  watch,
} from 'vue';
import OxdLabel from '@ohrm/oxd/core/components/Label/Label.vue';
import OxdSwitchInput from '@ohrm/oxd/core/components/Input/SwitchInput';
import MultiChips from '@ohrm/oxd/core/components/Input/MultiSelect/MultiSelectChips.vue';
import Status from '../constants/Status';
import {APIService} from '@/core/util/services/api.service';
import fileInput from '../../orangehrmPimPlugin/components/FileInput.vue';
import {required} from '@ohrm/core/util/validation/rules';

// Before
const props = defineProps({
  id: {type: Number, default: null},
  departments: {type: Array, default: () => []},
  isModal: {type: Boolean, default: false},
});

const emit = defineEmits(['onCancel', 'afterSubmit']);

const isLoading = ref(false);
const employees = ref([]);
const filterEmployees = ref([]);
const rules = ref({
  title: [required],
  version: [required],
  questions: {
    title: [required],
  },
  status: [required],
});

const showRequiredFiles = ref(false);

// Models
const questionModel = {
  title: '',
  options: [],
};

const optionModel = {
  label: '',
};

const http = new APIService(window.appGlobal.baseUrl, 'api/v2/pim/employees');

const files = [];

// Form
const form = reactive({
  id: props.id,
  title: '',
  version: '',
  dueDate: '',
  status: Status[0],

  description: '',

  questions: [
    {
      title: '',
      options: [],
    },
  ],
  comments: false,

  files,

  assignes: [],
  departments: [],
  departmentSubunits: [],

  removedQuestions: [],
  removedOptions: [],
});

const subUnits = ref([]);

onMounted(async () => {
  await http
    .getAll({
      includeEmployees: 'onlyCurrent',
      model: 'departments',
    })
    .then(response => {
      employees.value = response.data.data.map(employee => {
        return {
          id: employee.empNumber,
          label: `${employee.firstName} ${employee.middleName} ${employee.lastName}`,
          department: employee.department.id,
          subunit: employee.subUnit.id,
        };
      });

      filterEmployees.value = employees.value.filter(employee => {
        return employee.department == null && employee.subunit == null;
      });
    });

  if (props.id != null) {
    isLoading.value = true;
    http
      .request({
        method: 'GET',
        url: `api/training/information/${props.id}`,
      })
      .then(response => {
        form.title = response.data.code;
        form.version = response.data.version;
        form.dueDate = response.data.due_date;
        form.status = Status.filter(
          item => item.id === response.data.status,
        )[0];
        form.description = response.data.description;
        form.questions = response.data.questions;
        form.files = response.data.files;
        if (response.data.assigned_to !== null)
          form.assignes = employees.value.filter(employee => {
            return response.data.assigned_to.includes(employee.id);
          });

        if (response.data.departments !== null)
          form.departments = props.departments.filter(depart => {
            return response.data.departments.includes(depart.id);
          });

        setTimeout(() => {
          if (response.data.departmentSubunits !== null) {
            form.departmentSubunits = subUnits.value.filter(subunit => {
              return response.data.departmentSubunits.includes(subunit.id);
            });
          }
        }, 500);

        form.comments = response.data.comments;
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
});

const code = computed(() => {
  return `INF-${props.id}`;
});

const structuredClone = object => {
  return JSON.parse(JSON.stringify(object));
};

const optionsHasError = ref(false);

const onSave = () => {
  showRequiredFiles.value = false;
  if (form.files.length === 0) {
    showRequiredFiles.value = true;
    return;
  }

  const statusId = form.status.id;

  const files = form.files.map(item => {
    return item.value;
  });

  const formData = new FormData();
  if (form.id !== null) formData.append('id', form.id.toString());
  formData.append('code', form.title);
  formData.append('version', form.version);
  formData.append('dueDate', form.dueDate);
  formData.append('status', statusId);
  formData.append('description', form.description);

  files.forEach((item, index) => {
    formData.append('files[]', item);
    formData.append('fileVersions[]', form.files[index].version);
  });

  form.assignes.map(assign => {
    formData.append('assignes[]', assign.id);
  });

  if (form.departments.length === 0) form.departments = props.departments;

  form.departments.forEach(department => {
    formData.append('departments[]', department.id);
  });

  form.departmentSubunits.forEach(subunit => {
    formData.append('subunits[]', subunit.id);
  });

  form.questions.forEach(question => {
    formData.append('questions[]', JSON.stringify(question));
  });

  formData.append('comments', form.comments.toString());

  if (props.id != null) {
    form.removedQuestions.forEach(questionId => {
      formData.append('removedQuestions[]', questionId);
    });

    form.removedOptions.forEach(optionId => {
      formData.append('removedOptions[]', optionId);
    });
  }

  let url = `api/training/information`;

  if (props.id != null) {
    url = `${url}?_method=PUT`;
  }

  isLoading.value = true;

  http
    .request({
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      url: url,
      data: formData,
    })
    .then(response => {
      props.isModal
        ? emit('afterSubmit', {id: response.data.id, name: response.data.name})
        : window.location.assign(
            `${window.appGlobal.baseUrl}/training/viewInformation`,
          );
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const validateInput = () => {
  showRequiredFiles.value = false;
  if (form.files.length === 0) {
    showRequiredFiles.value = true;
  }

  optionsHasError.value = false;
  form.questions.forEach(question => {
    if (question.options.length === 0) {
      optionsHasError.value = true;
    }
  });
};

const addQuestion = () => {
  form.questions.push(structuredClone(questionModel));
};

const removeQuestion = index => {
  if (props.id != null) {
    if (form.questions[index])
      form.removedQuestions.push(form.questions[index]?.id);
  }

  form.questions.splice(index, 1);
};

const addOption = (question, event) => {
  const option = structuredClone(optionModel);
  option.label = event.target.value;

  question.options.push(option);

  event.target.value = '';
};

const removeOption = (question, label) => {
  const index = question.options.findIndex(item => item.label === label);

  if (props.id != null) {
    if (question.options[index])
      form.removedOptions.push(question.options[index]?.id);
  }

  question.options.splice(index, 1);
};

const addAttachment = event => {
  const id = crypto.randomUUID();
  const file = event.target.files[0];
  const fileName = file.name;
  form.files.push({
    id: id,
    label: fileName,
    version: '',
    value: file,
  });
};

const removeAttachment = id => {
  const index = form.files.findIndex(f => f.id === id);

  form.files.splice(index, 1);
};

const getDepartmentSubUnits = departments => {
  const departmentIds = departments.map(department => department.id);
  http
    .request({
      method: 'GET',
      url: `api/training/departments/sub-units`,
      params: {departmentIds},
    })
    .then(response => {
      subUnits.value = response.data;
      subUnits.value.unshift({
        id: 0,
        label: 'All',
      });
    });
};

watch(
  () => form.departments,
  departments => {
    if (departments.find(department => department.id === 0)) {
      departments = props.departments.filter(department => department.id != 0);
    }

    const departmentIds = departments.map(department => department.id);
    filterEmployees.value = employees.value.filter(employee => {
      return !departmentIds.includes(employee.department);
    });
    form.departmentSubunits = [];
    getDepartmentSubUnits(departments);
  },
);

watch(
  () => form.departmentSubunits,
  subUnits => {
    const departmentIds = form.departments.map(department => department.id);
    const subUnitIds = subUnits.map(subUnit => subUnit.id);
    filterEmployees.value = employees.value.filter(employee => {
      return (
        !departmentIds.includes(employee.department) &&
        !subUnitIds.includes(employee.subunit)
      );
    });
  },
);

const onCancel = () => {
  props.isModal
    ? emit('onCancel')
    : window.location.assign(
        `${window.appGlobal.baseUrl}/training/viewInformation`,
      );
};
</script>
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6">
        <template v-if="props.id"> Edit Information - {{ code }} </template>
        <template v-else>
          Add Information
        </template>
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submitValid.prevent="onSave">
        <oxd-form-row>
          <oxd-grid :cols="props.id ? 4 : 3" class="orangehrm-full-width-grid">
            <oxd-grid-item v-if="props.id">
              <oxd-input-field
                :value="`INF-${props.id}`"
                label="Code"
                placeholder="Code"
                disabled
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.title"
                label="Title"
                placeholder="Title"
                :rules="rules.title"
                required
              />
            </oxd-grid-item>
            <!--
                        <oxd-grid-item>
                            <oxd-input-field v-model="form.version" label="Version" placeholder="Version" :rules="rules.version" required />
                        </oxd-grid-item>
                        -->
            <oxd-grid-item>
              <date-input v-model="form.dueDate" label="Due Date" />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.status"
                label="Status"
                value="Select"
                type="select"
                :options="Status"
                :rules="rules.status"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="form.description"
            type="textarea"
            label="Description"
            placeholder="Description"
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="oxd-input-group">
                <file-input
                  :has-error="showRequiredFiles"
                  label="Add file"
                  required
                  @change="addAttachment"
                />
                <span
                  v-if="showRequiredFiles"
                  class="oxd-text oxd-text--span oxd-input-field-error-message oxd-input-group__message"
                >
                  Required
                </span>
              </div>
            </oxd-grid-item>
            <oxd-grid-item class="flex items-center">
              <MultiChips
                :selected="form.files"
                @chipRemoved="file => removeAttachment(file.id)"
              />
            </oxd-grid-item>
          </oxd-grid>
          <br />
          <oxd-grid :cols="8" class="orangehrm-full-width-grid">
            <oxd-grid-item v-for="file in form.files" :key="file.id">
              <oxd-input-field
                v-model="file.version"
                :label="`${file.label} - Version`"
                placeholder="Version"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <br />

        <oxd-form-row>
          <oxd-text tag="h6">
            Questions
          </oxd-text>
          <oxd-divider />
          <template v-for="(question, index) in form.questions" :key="index">
            <oxd-grid :cols="2" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="question.title"
                  :label="`Question ${index + 1}`"
                  placeholder="Untitled Question"
                  :rules="rules.questions.title"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item :class="{'flex gap-3': index >= 1}">
                <div class="w-85">
                  <oxd-input-field
                    :label="`Options`"
                    placeholder="Type and press enter to create option"
                    :class="{'oxd-input--error': optionsHasError}"
                    :required="true"
                    @keydown.enter.prevent="addOption(question, $event)"
                  >
                  </oxd-input-field>
                  <span
                    v-if="optionsHasError"
                    class="oxd-text oxd-text--span oxd-input-field-error-message oxd-input-group__message"
                  >
                    Required
                  </span>
                  <MultiChips
                    :selected="question.options"
                    @chipRemoved="
                      option => removeOption(question, option.label)
                    "
                  />
                </div>
                <div v-if="index >= 1" class="flex-column-direction">
                  <OxdLabel
                    label="Remove Question"
                    style="margin-bottom: 0.4rem;"
                  />
                  <oxd-icon-button
                    name="trash"
                    class="oxd-button--secondary w-fit"
                    @click="removeQuestion(index)"
                  />
                </div>
              </oxd-grid-item>
            </oxd-grid>
          </template>

          <OxdLabel label="Add question" style="margin-bottom: 0.4rem;" />
          <br />
          <oxd-icon-button
            name="plus"
            class="oxd-button--secondary"
            @click="addQuestion"
          />
        </oxd-form-row>
        <oxd-divider />
        <br />
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <div class="orangehrm-sm-field">
              <oxd-text tag="p" class="orangehrm-sm-field-label">
                Comments
              </oxd-text>
              <OxdSwitchInput v-model="form.comments" />
            </div>
          </oxd-grid-item>
        </oxd-grid>
        <br />
        <oxd-form-row>
          <div class="orangehrm-sm-field">
            <oxd-text tag="p" class="orangehrm-sm-field-label">
              Assigns
            </oxd-text>
          </div>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.departments"
                label="Departments"
                :value="
                  form.departments.length > 0 && form.departments?.[0].id === 0
                    ? 'All'
                    : 'Select'
                "
                type="multiselect"
                :options="
                  form.departments.length > 0 && form.departments?.[0].id === 0
                    ? [departments[0]]
                    : departments
                "
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.departmentSubunits"
                label="Sub units"
                :value="
                  form.departmentSubunits.length > 0 &&
                  form.departmentSubunits?.[0].id === 0
                    ? 'All'
                    : 'Select'
                "
                type="multiselect"
                :options="
                  form.departmentSubunits.length > 0 &&
                  form.departmentSubunits?.[0].id === 0
                    ? [subUnits[0]]
                    : subUnits
                "
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.assignes"
                label="Employees"
                value="Select"
                type="multiselect"
                :options="filterEmployees"
              />
            </oxd-grid-item>
            <!--
                        <oxd-grid-item class="flex items-center">
                            <oxd-button type="button" display-type="ghost" label="All Users" @click="setAllAssignes" />
                        </oxd-grid-item>
                        -->
          </oxd-grid>
        </oxd-form-row>
        <br />
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button @click="validateInput" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>
<style scoped>
.w-100 {
  width: 100%;
}

.w-85 {
  width: 85%;
}

.w-20 {
  width: 20%;
}

.w-fit {
  width: fit-content;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.flex-column-direction {
  display: flex;
  flex-direction: column;
}

.gap-3 {
  gap: 3px;
}
.oxd-input-group__message {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
}
.oxd-text {
  font-family: 'Nunito Sans';
  margin-top: 0;
  margin-bottom: 0;
}

.oxd-file-input-div {
  /*Opacity settings for all browsers*/
  opacity: 0;
  -moz-opacity: 0;
  filter: progid:DXImageTransform.Microsoft.Alpha(opacity=0);
}
</style>
